// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * DOM plumbing: modals, the event drawer, number formatting and the report
 * rendering. Kept apart from `main.ts` so the orchestration there stays legible.
 */

import { initGlossary } from './glossary';
import { mountEventDrawer } from './eventlog';

/* ─────────────────────────────── modals ─────────────────────────────── */

let openDialog: HTMLDialogElement | null = null;

export function initModals(): void {
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-modal]')) {
    button.addEventListener('click', () => showModal(button.dataset.modal ?? '', button));
  }
}

export function showModal(name: string, returnFocusTo?: HTMLElement): void {
  const template = document.getElementById(`tpl-${name}`) as HTMLTemplateElement | null;
  if (!template) return;
  closeModal();

  const dialog = document.createElement('dialog');
  dialog.className = 'modal';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'modal-close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  const body = document.createElement('div');
  body.className = 'modal-body';
  body.appendChild(template.content.cloneNode(true));

  dialog.append(close, body);
  document.body.appendChild(dialog);
  openDialog = dialog;

  const dismiss = () => {
    if (openDialog === dialog) openDialog = null;
    if (dialog.open) dialog.close();
    dialog.remove();
    returnFocusTo?.focus?.();
  };

  close.addEventListener('click', dismiss);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    dismiss();
  });
  dialog.addEventListener('mousedown', (event) => {
    if (event.target === dialog) dismiss();
  });

  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');

  initGlossary(body);
  close.focus();
}

export function closeModal(): boolean {
  if (!openDialog) return false;
  const dialog = openDialog;
  openDialog = null;
  if (dialog.open) dialog.close();
  dialog.remove();
  return true;
}

/* ─────────────────────────────── drawer ─────────────────────────────── */

export function initDrawer(): { close: () => boolean } {
  const drawer = document.getElementById('drawer') as HTMLElement;
  const toggle = document.getElementById('log-toggle') as HTMLButtonElement;
  const closeBtn = document.getElementById('drawer-close') as HTMLButtonElement;
  const body = document.getElementById('drawer-body') as HTMLElement;

  mountEventDrawer(body);

  const setOpen = (open: boolean) => {
    drawer.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('on', open);
    if (open) closeBtn.focus();
  };

  toggle.addEventListener('click', () => setOpen(drawer.hidden));
  // The drawer covers the toggle on a phone, so this button is the only way
  // back out — along with Escape, handled by the caller.
  closeBtn.addEventListener('click', () => {
    setOpen(false);
    toggle.focus();
  });

  return {
    close: () => {
      if (drawer.hidden) return false;
      setOpen(false);
      return true;
    },
  };
}

/* ───────────────────────────── formatting ───────────────────────────── */

export function formatNumber(value: number, digits = 0): string {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Significant-figure style: big numbers lose the noise, small ones keep detail. */
export function formatMeasure(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 1000) return formatNumber(value, 0);
  if (abs >= 100) return formatNumber(value, 1);
  if (abs >= 1) return formatNumber(value, 2);
  return formatNumber(value, 4);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}

/* ────────────────────────────── rendering ───────────────────────────── */

export interface KeyValue {
  label: string;
  value: string;
  term?: string;
}

export function renderKeyValues(container: HTMLElement, rows: KeyValue[]): void {
  container.innerHTML = '';
  for (const row of rows) {
    const dt = document.createElement('div');
    dt.className = 'kv-key';
    if (row.term) {
      const span = document.createElement('span');
      span.className = 'glossary-link';
      span.dataset.term = row.term;
      span.textContent = row.label;
      dt.appendChild(span);
    } else {
      dt.textContent = row.label;
    }
    const dd = document.createElement('div');
    dd.className = 'kv-val';
    dd.textContent = row.value;
    container.append(dt, dd);
  }
  initGlossary(container);
}

export type CheckState = 'ok' | 'warn' | 'bad';

export interface Check {
  label: string;
  state: CheckState;
  detail: string;
  term?: string;
}

const MARKS: Record<CheckState, string> = {
  ok: 'M20 6 9 17l-5-5',
  warn: 'M12 8v5M12 17h.01',
  bad: 'M18 6 6 18M6 6l12 12',
};

export function renderChecks(container: HTMLElement, checks: Check[]): void {
  container.innerHTML = '';
  for (const check of checks) {
    const li = document.createElement('li');
    li.className = 'check-row';
    li.dataset.state = check.state;

    const icon = document.createElement('span');
    icon.className = 'check-icon';
    icon.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${MARKS[check.state]}" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const text = document.createElement('div');
    text.className = 'check-text';
    const label = document.createElement('div');
    label.className = 'check-label';
    if (check.term) {
      const span = document.createElement('span');
      span.className = 'glossary-link';
      span.dataset.term = check.term;
      span.textContent = check.label;
      label.appendChild(span);
    } else {
      label.textContent = check.label;
    }
    const detail = document.createElement('div');
    detail.className = 'check-detail';
    detail.textContent = check.detail;
    text.append(label, detail);

    li.append(icon, text);
    container.appendChild(li);
  }
  initGlossary(container);
}
